import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const FUNCTIONS = [
  { id: "catalog", label: "Catalog", desc: "Price import + adding/removing/classifying items" },
  { id: "marketing", label: "Marketing", desc: "Product images, PDFs & the File Library" },
  { id: "quoting", label: "Quoting", desc: "Customers, portal link, Activity & Settings" },
];

const empty = { name: "", email: "", password: "", permissions: [], is_admin: false, disabled: false };

export default function StaffPanel() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", permissions: [...u.permissions], is_admin: u.is_admin, disabled: u.disabled });
    setOpen(true);
  };

  const togglePerm = (id) => setForm((f) => ({
    ...f, permissions: f.permissions.includes(id) ? f.permissions.filter((p) => p !== id) : [...f.permissions, id],
  }));

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!editing && form.password.length < 8) { toast.error("Set an initial password of at least 8 characters"); return; }
    if (editing && form.password && form.password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      if (editing) {
        const body = { name: form.name, email: form.email, permissions: form.permissions, is_admin: form.is_admin, disabled: form.disabled };
        if (form.password) body.password = form.password;
        await api.put(`/admin/users/${editing.id}`, body);
        toast.success("Staff account updated");
      } else {
        await api.post("/admin/users", { name: form.name, email: form.email, password: form.password, permissions: form.permissions, is_admin: form.is_admin });
        toast.success("Staff account created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? To only revoke access, edit them and turn on "Disabled" instead.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Staff account deleted");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Staff Accounts</h1>
          <p className="text-slate-500 mt-1">Create logins for your team and choose which functions each person can use.</p>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-slate-900 hover:bg-slate-800 transition-colors" data-testid="new-staff-button">
          <Plus className="h-4 w-4 mr-2" /> New staff account
        </Button>
      </header>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Access</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="staff-table">
            {users.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No staff accounts yet.</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 even:bg-slate-50/50 hover:bg-blue-50/40 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900">{u.name}{u.id === me?.id && <span className="text-slate-400 font-normal"> (you)</span>}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  {u.is_admin
                    ? <Badge className="rounded-sm bg-slate-900"><ShieldCheck className="h-3 w-3 mr-1" /> Administrator</Badge>
                    : (u.permissions.length
                        ? <div className="flex flex-wrap gap-1">{u.permissions.map((p) => <Badge key={p} variant="secondary" className="rounded-sm capitalize">{p}</Badge>)}</div>
                        : <span className="text-slate-400 text-xs">No functions assigned</span>)}
                </td>
                <td className="px-4 py-3 text-center">
                  {u.disabled
                    ? <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-medium"><XCircle className="h-3.5 w-3.5" /> Disabled</span>
                    : <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" title="Edit" onClick={() => openEdit(u)} data-testid={`edit-staff-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-red-500 hover:text-red-700 disabled:opacity-30" title="Delete" disabled={u.id === me?.id} onClick={() => remove(u)} data-testid={`delete-staff-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit staff account" : "New staff account"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Set name, email, password, functions and administrator access.</DialogDescription>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="staff-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="staff-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">{editing ? "Reset password (optional)" : "Initial password"}</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Leave blank to keep current password" : "Share this with the new user"} className="rounded-sm font-mono" data-testid="staff-password-input" />
              <p className="text-xs text-slate-400">They'll be prompted to change it after signing in.</p>
            </div>

            <div className="border border-slate-200 rounded-sm p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Administrator</Label>
                  <p className="text-xs text-slate-500">Full access + manage staff accounts</p>
                </div>
                <Switch checked={form.is_admin} onCheckedChange={(v) => setForm({ ...form, is_admin: v })} data-testid="staff-admin-switch" />
              </div>
              {!form.is_admin && (
                <div className="space-y-2 pt-1 border-t border-slate-100">
                  <Label className="text-xs uppercase tracking-[0.1em] text-slate-500 pt-2 block">Functions</Label>
                  {FUNCTIONS.map((fn) => (
                    <label key={fn.id} className="flex items-start gap-3 cursor-pointer py-1" data-testid={`staff-perm-${fn.id}`}>
                      <Checkbox checked={form.permissions.includes(fn.id)} onCheckedChange={() => togglePerm(fn.id)} className="mt-0.5" />
                      <span>
                        <span className="text-sm font-medium text-slate-800">{fn.label}</span>
                        <span className="block text-xs text-slate-500">{fn.desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {editing && editing.id !== me?.id && (
              <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2.5">
                <Label className="text-sm">Disabled (can't sign in)</Label>
                <Switch checked={form.disabled} onCheckedChange={(v) => setForm({ ...form, disabled: v })} data-testid="staff-disabled-switch" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={save} data-testid="save-staff-button">
              {busy ? "Saving…" : editing ? "Save changes" : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
